import type { Letter, Word } from "@repo/hindi-shared";
import { wordById, writingGuideById } from "@repo/hindi-shared";
import { AnimatePresence, motion, useReducedMotion } from "framer-motion";
import { useEffect, useRef } from "react";
import type { KeyboardEvent as ReactKeyboardEvent, ReactElement } from "react";

import type { AudioService } from "../audio";

import { DualLabel } from "./DualLabel";
import { InkButton } from "./InkButton";

/** Wrap every occurrence of `glyph` in a sindoor span within `text`. */
function highlightGlyph(
  text: string,
  glyph: string,
): (string | ReactElement)[] {
  if (glyph === "") return [text];
  const parts = text.split(glyph);
  const nodes: (string | ReactElement)[] = [];
  parts.forEach((part, index) => {
    if (part !== "") nodes.push(part);
    if (index < parts.length - 1) {
      nodes.push(
        <span key={index} className="text-sindoor">
          {glyph}
        </span>,
      );
    }
  });
  return nodes;
}

function exampleWords(exampleWordIds: readonly string[]): Word[] {
  const words: Word[] = [];
  for (const id of exampleWordIds) {
    const word = wordById.get(id);
    if (word !== undefined) words.push(word);
  }
  return words;
}

/**
 * Human-readable English name for a letter's romanization/aria label.
 *
 * A handful of signs (currently just halant) have an intentionally empty
 * `iast` in shared content — it has no vowel sound of its own to romanize
 * (see `shared/src/__tests__/content-lint.test.ts`'s explicit exception).
 * That's correct for shared content, but the web layer must not leave the
 * romanization line — or the "Listen: …" aria-label — blank for a learner
 * who cannot read the glyph yet. Fall back to a plain English name; extend
 * this map if another empty-`iast` letter is ever added.
 */
const FALLBACK_NAME_EN: Record<string, string> = {
  "sign-halant": "halant",
};

/** IAST if present, else a fallback English name for letters with no romanization. */
function displayName(letter: { id: string; iast: string }): string {
  return letter.iast !== "" ? letter.iast : (FALLBACK_NAME_EN[letter.id] ?? "");
}

interface LetterDetailProps {
  letter: Letter;
  audio: AudioService;
  onClose: () => void;
  /** Launches a single-exercise drill for this letter ("Practice"). */
  onPractice: () => void;
}

/**
 * LetterDetail — the overlay a learner sees after tapping a letter on the
 * Varnamala map: a huge glyph, its audio, IAST + hint, example words with the
 * letter picked out in sindoor, an external writing-guide link when one is
 * verified for this letter (see `writingGuideById`), and a launch button for
 * a focused single-letter drill. Closes on backdrop click or Esc.
 *
 * Scale/fade per the brief, honoring `prefers-reduced-motion` (no scale
 * animation, just an instant appearance) via `useReducedMotion`.
 */
export function LetterDetail({
  letter,
  audio,
  onClose,
  onPractice,
}: LetterDetailProps) {
  const prefersReducedMotion = useReducedMotion();
  const guide = writingGuideById.get(letter.id);
  const words = exampleWords(letter.exampleWordIds);
  const panelRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    function handleKeyDown(event: KeyboardEvent): void {
      if (event.key === "Escape") onClose();
    }
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [onClose]);

  // Focus containment: on open, remember the opener (the tapped Varnamala
  // tile) and move focus into the dialog; on close, hand focus back to it.
  // Mount-only by design — `letter` can change while the dialog stays open
  // (not currently, but cheap to be safe) without re-stealing focus.
  useEffect(() => {
    const previouslyFocused =
      document.activeElement instanceof HTMLElement
        ? document.activeElement
        : null;
    panelRef.current?.focus();
    return () => previouslyFocused?.focus();
  }, []);

  // Keep Tab cycling inside the dialog: wrap from the last focusable
  // element to the first (and shift+Tab from the first — or the panel
  // itself, right after open — back to the last).
  function handlePanelKeyDown(event: ReactKeyboardEvent<HTMLDivElement>): void {
    if (event.key !== "Tab") return;
    const panel = panelRef.current;
    if (panel === null) return;
    const focusable = panel.querySelectorAll<HTMLElement>(
      'a[href], button:not([disabled]), [tabindex]:not([tabindex="-1"])',
    );
    if (focusable.length === 0) return;
    const first = focusable[0]!;
    const last = focusable[focusable.length - 1]!;
    const active = document.activeElement;
    if (event.shiftKey) {
      if (active === first || active === panel) {
        event.preventDefault();
        last.focus();
      }
    } else if (active === last) {
      event.preventDefault();
      first.focus();
    }
  }

  function handleSpeak(): void {
    void audio.speak(letter.audioText);
  }

  const overlayAnimation = prefersReducedMotion
    ? {}
    : {
        initial: { opacity: 0 },
        animate: { opacity: 1 },
        exit: { opacity: 0 },
        transition: { duration: 0.15 },
      };

  const panelAnimation = prefersReducedMotion
    ? {}
    : {
        initial: { opacity: 0, scale: 0.96 },
        animate: { opacity: 1, scale: 1 },
        exit: { opacity: 0, scale: 0.96 },
        transition: { duration: 0.18, ease: "easeOut" as const },
      };

  return (
    <AnimatePresence>
      <motion.div
        role="presentation"
        className="fixed inset-0 z-50 flex items-center justify-center bg-ink/40 px-4"
        onClick={onClose}
        {...overlayAnimation}
      >
        <motion.div
          role="dialog"
          aria-modal="true"
          aria-label={displayName(letter)}
          ref={panelRef}
          tabIndex={-1}
          onClick={(event) => event.stopPropagation()}
          onKeyDown={handlePanelKeyDown}
          className="paper-grain relative w-full max-w-md border border-rule bg-paper px-8 py-10 text-center"
          {...panelAnimation}
        >
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="absolute top-3 right-4 font-latin text-xl text-ink-soft transition-colors duration-150 hover:text-sindoor"
          >
            ×
          </button>

          <button
            type="button"
            onClick={handleSpeak}
            aria-label={`Listen: ${displayName(letter)}`}
            className="font-dev text-[8rem] leading-none text-ink"
          >
            {letter.glyph}
          </button>

          <div className="mt-4">
            <p className="font-latin text-2xl text-ink-soft italic">
              {displayName(letter)}
            </p>
            <p lang="en" className="mt-2 font-latin text-base text-ink-soft">
              {letter.hint}
            </p>
          </div>

          {words.length > 0 && (
            <ul lang="hi" className="mt-6 flex flex-col gap-2">
              {words.map((word) => (
                <li key={word.id} className="font-dev text-2xl text-ink">
                  {highlightGlyph(word.devanagari, letter.glyph)}
                </li>
              ))}
            </ul>
          )}

          {guide !== undefined && (
            <div className="mt-6">
              <a
                href={guide.url}
                target="_blank"
                rel="noopener noreferrer"
                className="font-latin text-sm text-ink-soft underline transition-colors duration-150 hover:text-sindoor"
              >
                How it&apos;s written ↗
              </a>
              <p className="mt-2 font-latin text-xs text-ink-soft">
                Source: <span lang="en">Wikimedia Commons</span>
              </p>
            </div>
          )}

          <div className="mt-8">
            <InkButton variant="sindoor" onClick={onPractice}>
              <DualLabel variant="inline" en="Practice" hi="अभ्यास करें" />
            </InkButton>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}
